import React, { useEffect, useMemo, useRef, useState } from 'react';
import PieceCard from './PieceCard';

const generatePieceId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const createEmptyPiece = () => ({
  id: generatePieceId(),
  name: '',
  jobType: '',
  width: '',
  height: '',
  standardSize: '',
  materials: [],
  quantity: 1,
  individualItemsCount: undefined,
  finishes: [],
  machine_id: '',
});

const UniversalPieceManager = ({
  pieces,
  onPiecesChange,
  jobTypes = [],
  getMaterialsForJobType,
  getFinishesForJobType,
  title = '',
  maxPieces = 10,
  minPieces = 1,
  allowMultipleMaterialsForJobType,
  finishingMaterials = {},
  autoExpandKey = null,
  hideAddItemButton = false,
}) => {
  const [expandedIds, setExpandedIds] = useState([]);
  const lastAutoExpandKey = useRef(null);

  // Ensure there is at least one piece to work with
  useEffect(() => {
    if (!pieces.length) {
      const defaultPiece = createEmptyPiece();
      onPiecesChange([defaultPiece]);
      setExpandedIds([defaultPiece.id]);
    }
  }, [pieces, onPiecesChange]);

  // Keep expanded IDs in sync with incoming pieces (e.g., when loading saved quote)
  useEffect(() => {
    if (autoExpandKey != null && lastAutoExpandKey.current !== autoExpandKey) {
      const pieceIds = pieces.map((piece, index) => piece.id ?? index);
      setExpandedIds(pieceIds);
      lastAutoExpandKey.current = autoExpandKey;
      return;
    }

    if (pieces.length && expandedIds.length === 0) {
      setExpandedIds([pieces[0].id ?? 0]);
    }
  }, [pieces, expandedIds.length, autoExpandKey]);

  const handleAddPiece = () => {
    if (pieces.length >= maxPieces) {
      return;
    }

    const newPiece = createEmptyPiece();

    // Constraint: Inherit Job Type and Materials from the first piece if it exists
    if (pieces.length > 0) {
      const firstPiece = pieces[0];
      if (firstPiece.jobType) {
        newPiece.jobType = firstPiece.jobType;

        // Apply default finishes for this job type
        const selectedJobType = jobTypes.find(jt => jt.id === firstPiece.jobType);
        if (selectedJobType && selectedJobType.default_finishes) {
          newPiece.finishes = [...selectedJobType.default_finishes];
        }
      }
      if (firstPiece.materials && firstPiece.materials.length > 0) {
        // Clone the materials array to avoid reference issues
        newPiece.materials = firstPiece.materials.map(m => ({ ...m }));
      }
    }

    const nextPieces = [...pieces, newPiece];
    onPiecesChange(nextPieces);
    setExpandedIds((prev) => [...prev, newPiece.id]);
  };

  const handleRemovePiece = (pieceId) => {
    if (pieces.length <= minPieces) {
      return;
    }

    const filteredPieces = pieces.filter((piece) => (piece.id ?? piece) !== pieceId);
    onPiecesChange(filteredPieces);
    setExpandedIds((prev) => prev.filter((id) => id !== pieceId));
  };

  const handlePieceChange = (pieceId, updatedPiece) => {
    const nextPieces = pieces.map((piece, index) => {
      const currentId = piece.id ?? index; // Fallback if id missing (shouldn't happen with generatePieceId)

      // If updating the first piece, sync jobType and materials to all other pieces
      if (index === 0 && currentId === pieceId) {
        // Prepare the updated first piece
        const newFirstPiece = { ...piece, ...updatedPiece };

        // Return this piece as the new first piece
        return newFirstPiece;
      }

      // For other pieces, just update them normally
      if (currentId === pieceId) {
        return { ...piece, ...updatedPiece };
      }

      return piece;
    });

    // Second pass: If the first piece was updated, enforce constraints on others
    // We do this in a separate pass or just here?
    // Let's do a clean logic: 
    // If piece 0 changed JobType or Materials, apply to all.

    const firstPieceId = pieces[0]?.id ?? pieces[0];
    if (pieceId === firstPieceId) {
      // Check if jobType or materials changed in the update
      // updatedPiece only contains the *changes*.
      if (updatedPiece.jobType !== undefined || updatedPiece.materials !== undefined) {
        const newFirstPieceRaw = nextPieces[0];

        const synchronizedPieces = nextPieces.map((p, idx) => {
          if (idx === 0) return p;
          return {
            ...p,
            jobType: newFirstPieceRaw.jobType,
            materials: newFirstPieceRaw.materials.map(m => ({ ...m }))
          };
        });
        onPiecesChange(synchronizedPieces);
        return;
      }
    }

    onPiecesChange(nextPieces);
  };

  const toggleExpanded = (pieceId) => {
    setExpandedIds((prev) =>
      prev.includes(pieceId) ? prev.filter((id) => id !== pieceId) : [...prev, pieceId],
    );
  };

  const pieceSummary = useMemo(() => {
    const totalQuantity = pieces.reduce((total, piece) => {
      const quantity = Number(piece.quantity);
      return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
    }, 0);
    return { count: pieces.length, totalQuantity };
  }, [pieces]);

  const headerClasses = `mb-4 flex items-center ${title ? 'justify-between' : 'justify-start'
    }`;

  return (
    <section>
      <div className={headerClasses}>
        {title && <h3 className="text-lg font-medium text-gray-900">{title}</h3>}
        {/* No header summary when no title to avoid extra spacing */}
      </div>

      <div className="space-y-3">
        {pieces.map((piece, index) => {
          const pieceId = piece.id ?? index;
          const isExpanded = expandedIds.includes(pieceId);
          const allowRemove = pieces.length > minPieces;
          const allowMultipleMaterials =
            typeof allowMultipleMaterialsForJobType === 'function'
              ? allowMultipleMaterialsForJobType(piece.jobType)
              : true;

          // Lock JobType and Material for all pieces except the first one
          const isLocked = index > 0;

          return (
            <div key={pieceId} className="space-y-2">
              <PieceCard
                piece={piece}
                pieceNumber={index + 1}
                isExpanded={isExpanded}
                onToggle={() => toggleExpanded(pieceId)}
                onChange={(updatedPiece) => handlePieceChange(pieceId, updatedPiece)}
                onRemove={() => handleRemovePiece(pieceId)}
                jobTypes={jobTypes}
                getMaterialsForJobType={getMaterialsForJobType}
                getFinishesForJobType={getFinishesForJobType}
                allowRemove={allowRemove}
                allowMultipleMaterials={allowMultipleMaterials}
                finishingMaterials={finishingMaterials}
                isLocked={isLocked}
              />
              {!hideAddItemButton && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddPiece}
                    disabled={pieces.length >= maxPieces}
                    title={
                      'Click here to add another item using the same material, for an item with a different material/type please click the "Add to Quote" button below before adding'
                    }
                    className="rounded bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-200"
                  >
                    Add Item
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
        <span className="font-medium text-gray-900">{pieceSummary.count}</span>{' '}
        piece{pieceSummary.count === 1 ? '' : 's'} •{' '}
        <span className="font-medium text-gray-900">{pieceSummary.totalQuantity}</span> total items
      </div>
    </section>
  );
};

export default UniversalPieceManager;
